import { SidebarEventTypes } from '~/src/dash/analytics/analytics';
import { EBool } from '~/src/dash/api/types/bool';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  RouteAudienceManagerSettingsList,
  RouteBidderReportsList,
  RouteBidderSettingsList,
  RouteBrandSafetyList,
  RouteBrandsDashboard,
  RouteCampaignSettingsList,
  RouteChannelsView,
  RouteConfigurations,
  RouteContextualSegmentSettingsList,
  RouteCreativesLibraryList,
  RouteDashboards,
  RouteDealList,
  RouteFreewheelSiteList,
  RouteIABCategoryManager,
  RouteInventoryForecast,
  RouteInventoryRouteList,
  RouteMeasurementDashboard,
  RouteMeasurementDrill,
  RouteMonitorList,
  RouteNetworkDashboard,
  RouteNetworkList,
  RouteOrderList,
  RouteReportNew,
  RouteResellerManagerList,
  RouteSandbox,
  RouteSSPReportList,
} from '~/src/dash/router/routes';
import {
  isAudienceRouteDisabled,
  isCampaignRouteDisabled,
  isDealRouteDisabled,
} from '~/src/dash/utils/ui';
import { IRoute } from '~/src/dash/views/navbar/NavBar/common/types';

export const REPORT_REFACTOR_TEAM = [
  '2170', // nathan
  '1921', // brent
  '2775', // jon
  '3375', // elson
  '3886', // tiffany
  '3373', // ansul
  '3425', // clem
  '336', // clem
  '167', // monica
]; // TODO: delete after reporting refactor is finished

export type NavBarItemLabel =
  // main
  | 'Dashboard'
  | 'Dashboards'
  | 'Reporting'
  | 'Reports Dashboard'
  | 'Report Builder'
  // demand
  | 'Bidders'
  | 'Campaigns'
  | 'Deals'
  // inventory
  | 'Channels'
  | 'Routes'
  | 'Segments'
  // misc
  | 'Brand Safety'
  | 'Measurement'
  | 'Brands Dashboard'
  | 'Ad Insertion'
  | 'Tools'
  | 'Configurations'
  | 'References'
  | 'Glossary'
  | 'Help'
  // admin
  | 'SSP Analytics'
  | 'IAB Category'
  | 'Reseller'
  | 'Freewheel Sites'
  | 'Sandbox'
  // super user only testing
  | 'Dashboards Beta'
  | 'Reporting Beta';

export const NavBarItemLabels: Record<string, NavBarItemLabel> = {
  // main
  DASHBOARD: 'Dashboard',
  DASHBOARDS: 'Dashboards',
  REPORTING: 'Reporting',
  REPORTSDASHBOARD: 'Reports Dashboard',
  REPORTBUILDER: 'Report Builder',
  // demand
  BIDDERS: 'Bidders',
  CAMPAIGNS: 'Campaigns',
  DEALS: 'Deals',
  // inventory
  CHANNELS: 'Channels',
  ROUTES: 'Routes',
  SEGMENTS: 'Segments',
  // misc
  BRAND_SAFETY: 'Brand Safety',
  MEASUREMENT: 'Measurement',
  BRANDS_DASHBOARD: 'Brands Dashboard',
  AD_INSERTION: 'Ad Insertion',
  TOOLS: 'Tools',
  CONFIGURATIONS: 'Configurations',
  REFERENCES: 'References',
  GLOSSARY: 'Glossary',
  HELP: 'Help',
  // admin
  SSP_ANALYTICS: 'SSP Analytics',
  IAB_CATEGORY: 'IAB Category',
  RESELLER: 'Reseller',
  FREEWHEEL_SITES: 'Freewheel Sites',
  SANDBOX: 'Sandbox',
  // super user only testing
  REPORTING_BETA: 'Reporting Beta',
};

export interface IPermissionParams {
  user: IUserState;
  session: ISessionState;
  isDemoUser: boolean;
  isSuperUser: boolean;
  isMeasurementEnabled: boolean | number;
}

export interface INavBarSingleRow {
  label: string;
  iconPath: string;
  iconPathDark: string;
  eventName: SidebarEventTypes;
  route?: IRoute;
  permChecks?: string[];
  isSubMenu?: boolean | undefined;
  subViews?: string[] | undefined;
  subItems?: INavBarSubItem[];
  isSuperUserOnly?: boolean;
  isExternalRoute?: boolean;
  // this function should return true if the item should be hidden
  checkIfHidden?: (permissionParams: IPermissionParams) => boolean;
}

export interface INavBarSubItem {
  label: string;
  eventName: SidebarEventTypes;
  route: IRoute;
  permChecks?: string[];
  pathString: string;
  customActiveCheck?: (pathname: string) => boolean;
}
export interface INavBarConfig {
  sections: {
    [key: string]: {
      label: string;
      isLabelDisplayed: boolean;
      items: INavBarSingleRow[];
    };
  };
}

export const navBarConfig = {
  sections: {
    Main: {
      label: 'Main',
      isLabelDisplayed: false,
      items: [
        {
          label: NavBarItemLabels.DASHBOARDS,
          iconPath: '/assets/shared/svg/navbar/Dashboards16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Dashboards16.svg',
          eventName: 'Sidebar - Home',
          route: RouteDashboards,
        },
        {
          label: NavBarItemLabels.REPORTING,
          iconPath: '/assets/shared/svg/navbar/Reporting16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Reporting16.svg',
          eventName: 'Sidebar - Reporting',
          route: RouteBidderReportsList,
          permChecks: ['read_report', 'write_report'],
        },
      ] as INavBarSingleRow[],
    },
    Demand: {
      label: 'Demand',
      isLabelDisplayed: true,
      items: [
        {
          label: NavBarItemLabels.BIDDERS,
          iconPath: '/assets/shared/svg/navbar/Bidders16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Bidders16.svg',
          eventName: 'Sidebar - Bidders',
          route: RouteBidderSettingsList,
          permChecks: ['read_bidder', 'write_bidder'],
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.CAMPAIGNS,
          iconPath: '/assets/shared/svg/navbar/Campaigns16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Campaigns16.svg',
          permChecks: ['read_campaign', 'write_campaign'],
          subViews: ['orders', 'line-items', 'creative-library'],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return isCampaignRouteDisabled(permissionParams.session.publisher);
          },
          subItems: [
            {
              label: 'Orders',
              eventName: 'Sidebar - Orders',
              route: RouteOrderList,
              pathString: 'orders',
              customActiveCheck: (pathname) =>
                pathname.indexOf('orders') > -1 && !(pathname.indexOf('line-items') > -1),
            },
            {
              label: 'Line Items',
              eventName: 'Sidebar - Line Items',
              route: RouteCampaignSettingsList,
              pathString: 'line-items',
            },
            {
              label: 'Creative Library',
              eventName: 'Sidebar - Creative Library',
              route: RouteCreativesLibraryList,
              pathString: 'creative',
            },
          ],
        },
        {
          label: NavBarItemLabels.DEALS,
          iconPath: '/assets/shared/svg/navbar/Deals16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Deals16.svg',
          eventName: 'Sidebar - Deals',
          route: RouteDealList,
          permChecks: ['read_deal', 'write_deal'],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return isDealRouteDisabled(permissionParams.session.publisher);
          },
        },
      ] as INavBarSingleRow[],
    },
    Inventory: {
      label: 'Inventory',
      isLabelDisplayed: true,
      items: [
        {
          label: NavBarItemLabels.CHANNELS,
          iconPath: '/assets/shared/svg/navbar/Channels16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Channels16.svg',
          eventName: 'Sidebar - Channels',
          route: RouteChannelsView,
          permChecks: ['read_channel', 'write_channel'],
        },
        {
          label: NavBarItemLabels.ROUTES,
          iconPath: '/assets/shared/svg/route.svg',
          iconPathDark: '/assets/shared/svg/route_black.svg',
          eventName: 'Sidebar - Routes',
          route: RouteInventoryRouteList,
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.SEGMENTS,
          iconPath: '/assets/shared/svg/navbar/Segments16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Segments16.svg',
          subViews: ['audience', 'contextual'],
          subItems: [
            {
              label: 'Audience',
              eventName: 'Sidebar - Audience Segments',
              route: RouteAudienceManagerSettingsList,
              permChecks: ['read_audience_manager', 'write_audience_manager'],
              pathString: 'audience',
              customActiveCheck: (pathname) =>
                pathname.indexOf('segments') > -1 && pathname.indexOf('audience') > -1,
            },
            {
              label: 'Contextual',
              eventName: 'Sidebar - Contextual Segments',
              route: RouteContextualSegmentSettingsList,
              pathString: 'contextual',
              customActiveCheck: (pathname) =>
                pathname.indexOf('segments') > -1 && pathname.indexOf('contextual') > -1,
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return isAudienceRouteDisabled(permissionParams.session.publisher);
          },
        },
      ] as INavBarSingleRow[],
    },
    Miscellaneous: {
      label: 'Miscellaneous',
      isLabelDisplayed: false,
      items: [
        {
          label: NavBarItemLabels.BRAND_SAFETY,
          iconPath: '/assets/shared/svg/navbar/BrandSafety16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/BrandSafety16.svg',
          eventName: 'Sidebar - Brand Safety',
          route: RouteBrandSafetyList,
          permChecks: ['read_channel_brandsafety_rule', 'write_channel_brandsafety_rule'],
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.MEASUREMENT,
          iconPath: '/assets/shared/svg/navbar/Measurement16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Measurement16.svg',
          subViews: ['measurement-dashboard', 'measurement-drill'],
          subItems: [
            {
              label: 'Overview',
              eventName: 'Sidebar - Measurement Dashboard',
              route: RouteMeasurementDashboard,
              pathString: 'measurement-dashboard',
            },
            {
              label: 'Drill',
              eventName: 'Sidebar - Measurement Drill',
              route: RouteMeasurementDrill,
              pathString: 'measurement-drill',
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !(permissionParams.isSuperUser || permissionParams.isMeasurementEnabled);
          },
        },
        {
          label: NavBarItemLabels.BRANDS_DASHBOARD,
          iconPath: '/assets/shared/svg/navbar/BrandsDash16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/BrandsDash16.svg',
          eventName: 'Sidebar - Brands Dashboard',
          route: RouteBrandsDashboard,
        },
        {
          isSubMenu: true,
          label: 'Ad Insertion',
          iconPath: '/assets/shared/svg/navbar/AdInsertion16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/AdInsertion16.svg',
          permChecks: ['read_network', 'write_network'],
          subViews: ['network-dashboard', 'network-list', 'network'],
          subItems: [
            {
              label: 'Dashboard',
              eventName: 'Sidebar - SSAI Dashboard',
              route: RouteNetworkDashboard,
              pathString: 'network-dashboard',
            },
            {
              label: 'Networks',
              eventName: 'Sidebar - Network',
              route: RouteNetworkList,
              pathString: 'network-list',
              customActiveCheck: (pathname) =>
                (pathname.indexOf('network-list') > -1 || pathname.indexOf('network') > -1) &&
                !(pathname.indexOf('network-dashboard') > -1),
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return permissionParams.session.publisher.NetworkEnabled !== EBool.EBool_TRUE;
          },
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.TOOLS,
          iconPath: '/assets/shared/svg/navbar/Tools16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Tools16.svg',
          subViews: ['inventory-forecast', 'monitor'],
          subItems: [
            {
              label: 'Inventory Forecast',
              eventName: 'Sidebar - Inventory Forecast',
              route: RouteInventoryForecast,
              pathString: 'inventory-forecast',
            },
            {
              label: 'Monitors',
              eventName: 'Sidebar - Monitors',
              route: RouteMonitorList,
              pathString: 'monitor',
            },
          ],
        },
        {
          label: NavBarItemLabels.CONFIGURATIONS,
          iconPath: '/assets/shared/svg/navbar/Configurations16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Configurations16.svg',
          eventName: 'Sidebar - Configurations',
          route: RouteConfigurations,
        },
      ] as INavBarSingleRow[],
    },
    Admin: {
      label: 'Admin',
      isLabelDisplayed: true,
      items: [
        {
          label: NavBarItemLabels.SSP_ANALYTICS,
          iconPath: '/assets/shared/svg/navbar/SSP16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/SSP16.svg',
          eventName: 'Sidebar - SSP Analytics',
          route: RouteSSPReportList,
          isSuperUserOnly: true,
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !(permissionParams.isSuperUser && permissionParams.session.publisher.ID === '1');
          },
        },
        {
          label: NavBarItemLabels.IAB_CATEGORY,
          iconPath: '/assets/shared/svg/navbar/IABCat16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/IABCat16.svg',
          eventName: 'Sidebar - IAB Category',
          route: RouteIABCategoryManager,
          isSuperUserOnly: true,
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !permissionParams.isSuperUser;
          },
        },
        {
          label: NavBarItemLabels.RESELLER,
          iconPath: '/assets/shared/svg/reseller.svg',
          iconPathDark: '/assets/shared/svg/reseller_black.svg',
          eventName: 'Sidebar - Reseller',
          route: RouteResellerManagerList,
          isSuperUserOnly: true,
        },
        {
          label: NavBarItemLabels.FREEWHEEL_SITES,
          iconPath: '/assets/shared/svg/reseller.svg',
          iconPathDark: '/assets/shared/svg/reseller_black.svg',
          eventName: 'Sidebar - Freewheel Sites',
          route: RouteFreewheelSiteList,
          isSuperUserOnly: true,
        },
        {
          label: NavBarItemLabels.SANDBOX,
          iconPath: '/assets/shared/svg/navbar/Dashboards16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Dashboards16.svg',
          eventName: 'Sidebar - Sandbox',
          route: RouteSandbox,
          isSuperUserOnly: true,
        },
        {
          label: NavBarItemLabels.REPORTING_BETA,
          iconPath: '/assets/shared/svg/navbar/Reporting16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Reporting16.svg',
          eventName: 'Sidebar - Reporting Beta',
          route: RouteReportNew,
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !REPORT_REFACTOR_TEAM.includes(permissionParams.user.user.ID);
          },
        },
      ] as INavBarSingleRow[],
    },
  },
} as INavBarConfig;
